// Shared Inchstone Hierarchy Component
// Reusable component for displaying and managing hierarchical inchstones
// Used in: TransformationDetailPage, TransformationFormPage, and other places

import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { 
  PlusIcon, 
  CheckIcon, 
  ClockIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

// Inchstone interface - matches the structure used across the application
export interface Inchstone {
  id: string;
  name: string;
  description: string;
  start_date?: string;
  due_date?: string;
  task_type: string;
  status: string;
  priority: string;
  parent_id?: string;
  transformation_id: string;
  owner_user_id: string;
  tenant_id: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  completed_by?: string;
  owner_name?: string;
  children_count?: number;
  children?: Inchstone[];
}

// Props for InchstoneHierarchy component
export interface InchstoneHierarchyProps {
  inchstones: Inchstone[];
  onEdit: (inchstone: Inchstone) => void;
  onDelete: (id: string) => void;
  onToggleStatus: (id: string, status: string) => void;
  onCreateChild: (parentId?: string) => void;
  onReorder: (inchstoneId: string, newSortOrder: number, newParentId?: string) => void;
  level?: number;
  hideStatusToggle?: boolean; // Hide status toggle button (e.g., for templates)
  templateMode?: boolean; // Template mode - uses soft colors and disables date-based coloring
}

/**
 * InchstoneHierarchy - Recursive component for displaying hierarchical inchstones
 * Supports drag-and-drop reordering and nested children
 */
export const InchstoneHierarchy: React.FC<InchstoneHierarchyProps> = ({
  inchstones,
  onEdit,
  onDelete,
  onToggleStatus,
  onCreateChild,
  onReorder,
  level = 0,
  hideStatusToggle = false,
  templateMode = false
}) => {
  const { t } = useLanguage();

  const handleRootDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLElement).style.backgroundColor = '';
    const draggedId = e.dataTransfer.getData('text/plain');
    console.log('Root drop event:', { draggedId });
    if (draggedId) {
      // Make the dragged item root-level (no parent)
      console.log('Calling onReorder for root:', { draggedId });
      onReorder(draggedId, 0, undefined);
    }
  };

  const handleRootDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    // Use theme primary color with opacity for visual feedback
    const root = document.documentElement;
    const primaryBg = getComputedStyle(root).getPropertyValue('--color-primary-bg').trim();
    // Convert hex to rgba with 10% opacity
    const hex = primaryBg.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    (e.currentTarget as HTMLElement).style.backgroundColor = `rgba(${r}, ${g}, ${b}, 0.1)`;
  };

  const handleRootDragLeave = (e: React.DragEvent) => {
    e.stopPropagation();
    (e.currentTarget as HTMLElement).style.backgroundColor = '';
  };

  return (
    <div className="space-y-2">
      {/* Root-level drop zone */}
      {level === 0 && (
        <div 
          className="min-h-4 border-2 border-dashed border-[var(--color-border)] rounded-lg p-2 text-center text-[var(--color-surface-text)]/60 text-sm"
          onDragOver={handleRootDragOver}
          onDragLeave={handleRootDragLeave}
          onDrop={handleRootDrop}
        >
          {t('transformation.dropHereToMakeRootLevel')}
        </div>
      )}
      {inchstones.map((inchstone) => (
        <div key={inchstone.id} className="space-y-1">
          {/* Main inchstone with indentation */}
          <div 
            className="flex items-start"
            style={{ 
              marginLeft: `${level * 2}rem`,
              paddingLeft: level > 0 ? '1rem' : '0'
            }}
          >
            {/* Tree connector for children */}
            {level > 0 && (
              <div className="flex items-center mr-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <div className="w-0.5 h-4 bg-[var(--color-border)]"></div>
                </div>
                <div className="w-2 h-0.5 bg-[var(--color-border)]"></div>
              </div>
            )}
            
            <div className="flex-1">
              <InchstoneRow
                inchstone={inchstone}
                onEdit={onEdit}
                onDelete={onDelete}
                onToggleStatus={onToggleStatus}
                onCreateChild={() => onCreateChild(inchstone.id)}
                onReorder={onReorder}
                hideStatusToggle={hideStatusToggle}
                templateMode={templateMode}
              />
            </div>
          </div>
          
          {/* Render children with proper indentation */}
          {inchstone.children && inchstone.children.length > 0 && (
            <InchstoneHierarchy
              inchstones={inchstone.children}
              onEdit={onEdit}
              onDelete={onDelete}
              onToggleStatus={onToggleStatus}
              onCreateChild={onCreateChild}
              onReorder={onReorder}
              level={level + 1}
              hideStatusToggle={hideStatusToggle}
              templateMode={templateMode}
            />
          )}
        </div>
      ))}
    </div>
  );
};

// Props for InchstoneRow component
export interface InchstoneRowProps {
  inchstone: Inchstone;
  onEdit: (inchstone: Inchstone) => void;
  onDelete: (id: string) => void;
  onToggleStatus: (id: string, status: string) => void;
  onCreateChild: () => void;
  onReorder: (inchstoneId: string, newSortOrder: number, newParentId?: string) => void;
  hideStatusToggle?: boolean; // Hide status toggle button (e.g., for templates)
  templateMode?: boolean; // Template mode - uses soft colors and disables date-based coloring
}

/**
 * InchstoneRow - Individual inchstone row with drag-and-drop and action buttons
 * Displays inchstone information and provides edit/delete/toggle status actions
 */
export const InchstoneRow: React.FC<InchstoneRowProps> = ({
  inchstone,
  onEdit,
  onDelete,
  onToggleStatus,
  onCreateChild,
  onReorder,
  hideStatusToggle = false,
  templateMode = false
}) => {
  const { t } = useLanguage();

  // Determine row color based on status - using theme colors
  const getRowColor = (inchstone: Inchstone) => {
    // Template mode: use soft theme colors (accent background)
    if (templateMode) {
      return 'bg-[var(--color-accent-bg)] border-[var(--color-border)]';
    }
    
    if (inchstone.status === 'COMPLETED') {
      return 'bg-[var(--color-success)]/10 border-[var(--color-success)]/30';
    }
    
    // Only check for overdue dates if not in template mode
    if (!templateMode && inchstone.due_date) {
      const dueDate = new Date(inchstone.due_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (dueDate < today) {
        return 'bg-[var(--color-error)]/10 border-[var(--color-error)]/30';
      }
    }
    
    return 'bg-[var(--color-info)]/10 border-[var(--color-info)]/30';
  };

  const handleDragStart = (e: React.DragEvent) => {
    e.stopPropagation();
    e.dataTransfer.setData('text/plain', inchstone.id);
    e.dataTransfer.effectAllowed = 'move';
    console.log('Drag started:', inchstone.id, inchstone.name);
    // Add visual feedback
    (e.currentTarget as HTMLElement).style.opacity = '0.5';
  };

  const handleDragEnd = (e: React.DragEvent) => {
    e.stopPropagation();
    // Reset visual feedback
    (e.currentTarget as HTMLElement).style.opacity = '1';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    // Add visual feedback for drop zones using theme colors
    const root = document.documentElement;
    const primaryBg = getComputedStyle(root).getPropertyValue('--color-primary-bg').trim();
    // Convert hex to rgba with 10% opacity
    const hex = primaryBg.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    (e.currentTarget as HTMLElement).style.backgroundColor = `rgba(${r}, ${g}, ${b}, 0.1)`;
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.stopPropagation();
    // Remove visual feedback
    (e.currentTarget as HTMLElement).style.backgroundColor = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLElement).style.backgroundColor = '';
    const draggedId = e.dataTransfer.getData('text/plain');
    console.log('Drop event:', { draggedId, targetId: inchstone.id, targetName: inchstone.name });
    if (draggedId && draggedId !== inchstone.id) {
      // Move the dragged inchstone to be a child of this inchstone
      console.log('Calling onReorder:', { draggedId, newParentId: inchstone.id });
      onReorder(draggedId, 0, inchstone.id);
    } else {
      console.log('Drop ignored - same ID or no draggedId');
    }
  };

  return (
    <div 
      className={`${getRowColor(inchstone)} border rounded-lg p-1 hover:shadow-md transition-shadow group`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 flex items-center space-x-1">
          {/* Drag handle - only this element is draggable */}
          <div 
            className="p-0.5 text-[var(--color-surface-text)]/60 hover:text-[var(--color-surface-text)] cursor-move flex-shrink-0"
            title={t('transformation.dragToReorder')}
            draggable
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/>
            </svg>
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-1">
              {/* Tree indicator */}
              <div className="text-[var(--color-surface-text)]/60 text-xs">
                {inchstone.children && inchstone.children.length > 0 ? '📁' : '📄'}
              </div>
              <h3 
                className={`text-sm font-medium ${
                  templateMode
                    ? 'text-[var(--color-accent-text)]'
                    : inchstone.status === 'COMPLETED' 
                    ? 'line-through text-[var(--color-success)]' 
                    : !templateMode && inchstone.due_date && new Date(inchstone.due_date) < new Date()
                    ? 'text-[var(--color-error)]'
                    : 'text-[var(--color-info)]'
                }`}
                title={inchstone.description || undefined}
              >
                {inchstone.name}
                {inchstone.due_date && !templateMode && (
                  <span className="ml-1 font-normal">
                    - Due {new Date(inchstone.due_date).toLocaleDateString()}
                  </span>
                )}
              </h3>
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-1">
          {!hideStatusToggle && (
            <button
              onClick={() => onToggleStatus(inchstone.id, inchstone.status)}
              className={`w-6 h-6 rounded-full transition-colors ${
                templateMode
                  ? 'bg-[var(--color-primary-bg)] hover:opacity-80'
                  : inchstone.status === 'COMPLETED' 
                  ? 'bg-[var(--color-success)] hover:opacity-80' 
                  : !templateMode && inchstone.due_date && new Date(inchstone.due_date) < new Date()
                  ? 'bg-[var(--color-error)] hover:opacity-80'
                  : 'bg-[var(--color-warning)] hover:opacity-80'
              }`}
              title={
                inchstone.status === 'COMPLETED' 
                  ? t('transformation.markAsIncomplete')
                  : inchstone.due_date && new Date(inchstone.due_date) < new Date()
                  ? t('transformation.pastDueClickToMarkComplete')
                  : t('transformation.markAsComplete')
              }
            >
              {inchstone.status === 'COMPLETED' ? (
                <CheckIcon className="w-3 h-3 text-white mx-auto" />
              ) : (
                <ClockIcon className="w-3 h-3 text-white mx-auto" />
              )}
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onCreateChild(); }}
            onMouseDown={(e) => e.stopPropagation()}
            className="p-0.5 text-[var(--color-surface-text)]/60 hover:text-[var(--color-surface-text)]"
            title={t('transformation.addChildInchstone')}
          >
            <PlusIcon className="w-3 h-3" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(inchstone); }}
            onMouseDown={(e) => e.stopPropagation()}
            className="p-0.5 text-[var(--color-surface-text)]/60 hover:text-[var(--color-surface-text)]"
            title={t('transformation.editInchstone')}
          >
            <PencilIcon className="w-3 h-3" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(inchstone.id); }}
            onMouseDown={(e) => e.stopPropagation()}
            className="p-0.5 text-[var(--color-surface-text)]/60 hover:text-[var(--color-error)]"
            title={t('transformation.deleteInchstone')}
          >
            <TrashIcon className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
};

